import { getInitials } from "@frontend-shared/utils";
import { getUserHandle } from "@frontend-shared/utils/user";
import { MoreVertical, Trash2 } from "lucide-react";
import { memo, useCallback } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { IShareAccessRowProps } from "./types";

export const ShareAccessRow = memo(
  ({
    entry,
    shouldShowMenu,
    isRemoving,
    onRemove,
    isSelectable,
    isSelected,
    isBulkPending,
    onSelectChange,
    shouldReserveCheckboxSpace,
    shouldReserveMenuSpace,
    labels,
  }: IShareAccessRowProps) => {
    // NOTE: NSAI-7736 — a person with no email is named, and identified on the
    // second line, by their IdP login name instead of "Unknown" over a blank.
    const handle = getUserHandle({
      email: entry.email,
      username: entry.username,
    });
    const displayName = entry.fullName || handle || labels.unknownUser;
    const initials = getInitials(displayName);
    // NOTE: Only an explicit false is a revocation — undefined means the
    // consumer does not track effective access, so those rows keep the normal
    // permission badge. Requires the copy too, since a domain without
    // no-access labels has nothing to show in place of it (NSAI-4540).
    const hasNoAccess = entry.canView === false && !!labels.noAccess;

    const handleSelectChange = useCallback(
      (checked: boolean | "indeterminate") => {
        onSelectChange(entry.id, checked === true);
      },
      [onSelectChange, entry.id],
    );

    return (
      <div
        className="flex items-center justify-between gap-3"
        data-testid={`share-row-${entry.id}`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {isSelectable ? (
            <Checkbox
              checked={isSelected}
              onCheckedChange={handleSelectChange}
              disabled={isBulkPending || isRemoving}
              aria-label={labels.formatSelectAria(displayName)}
              data-testid={`share-row-select-${entry.id}`}
            />
          ) : (
            shouldReserveCheckboxSpace && (
              <div className="h-4 w-4 shrink-0" aria-hidden="true" />
            )
          )}
          <Avatar
            className={cn(
              "h-9 w-9 shrink-0 border-0 bg-primary/10",
              hasNoAccess && "opacity-60",
            )}
          >
            <AvatarImage src="" />
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
              {entry.avatarIcon ?? initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p
              dir="auto"
              className={cn(
                "truncate text-sm font-medium rtl:text-right",
                hasNoAccess && "text-muted-foreground",
              )}
              data-testid={`share-row-name-${entry.id}`}
            >
              {displayName}
            </p>
            <p
              dir="auto"
              className="truncate text-xs text-muted-foreground rtl:text-right"
              data-testid={`share-row-handle-${entry.id}`}
            >
              {handle}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {hasNoAccess && labels.noAccess ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                {/* NOTE: The trigger is a native button wrapping the badge
                    rather than the badge itself. Badge renders a plain div, so
                    as the trigger it was reachable by mouse only — keyboard and
                    screen-reader users got "No access" with no explanation of
                    why. A button is focusable, so the tooltip opens on focus
                    too. (Badge is also not a forwardRef component, which makes
                    it a poor asChild target in general.) (NSAI-4540) */}
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid={`share-row-no-access-${entry.id}`}
                  >
                    <Badge
                      variant="outline"
                      className="border-destructive/40 px-2.5 py-0.5 text-xs font-medium text-destructive"
                    >
                      {labels.noAccess.badge}
                    </Badge>
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-xs break-words"
                  data-testid={`share-row-no-access-tooltip-${entry.id}`}
                >
                  {labels.noAccess.tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Badge className="bg-primary/10 hover:bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
              {entry.badgeLabel ?? labels.permissionBadge}
            </Badge>
          )}

          {shouldShowMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-9"
                  disabled={isRemoving || isBulkPending}
                  data-testid={`share-row-menu-${entry.id}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="cursor-pointer text-destructive focus:text-destructive"
                  onClick={() => onRemove(entry.id, entry.isCurrentUser)}
                  data-testid={`share-row-remove-${entry.id}`}
                >
                  <Trash2 className="me-2 h-4 w-4" />
                  {entry.isCurrentUser ? labels.leaveLabel : labels.removeLabel}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* NOTE: NSAI-6773 — a row without a menu still holds the menu's slot
              when another row shows one, so every badge (Host included) lines
              up in one column. */}
          {!shouldShowMenu && shouldReserveMenuSpace && (
            <div className="h-8 w-9 shrink-0" aria-hidden="true" />
          )}
        </div>
      </div>
    );
  },
);

ShareAccessRow.displayName = "ShareAccessRow";
